import * as React from "react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useStore } from "../../helpers";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "@ag-grid-community/react";
import Columns from "./task-list-components/Columns";
import { toJS } from "mobx";
import { getTaskRows } from "./task-list-components/utilFunctions";
import {
  TaskCard,
  TaskCardSkeleton,
  TaskTableSkeleton,
} from "./task-components";

interface TaskListProps {
  viewMode?: "card" | "table";
}

export const TaskList = observer(({ viewMode = "table" }: TaskListProps) => {
  const rootStore = useStore();
  const { taskManagerStore } = rootStore;
  const {
    task_filters: { userList, status, pivLevel1 },
    task_count_per_page,
    tasklist_current_page_number,
    filtered_task_list,
    loading,
  } = taskManagerStore;
  const params = useParams();
  const [t] = useTranslation("task_manager");
  const projectid = params.projectid;
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const totalPages = Math.ceil(total / task_count_per_page);

  const handlePageChange = async (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    taskManagerStore.update_tm_store_prop(
      "tasklist_current_page_number",
      value
    );
    window.sessionStorage.setItem("pageno", value.toString());
  };

  const columnDefs = Columns();
  const rowData = getTaskRows(filtered_task_list);

  useEffect(() => {
    fetch_task_list();
  }, [projectid, userList, status, pivLevel1, tasklist_current_page_number, task_count_per_page]);

  const fetch_task_list = async () => {
    setIsLoading(true);
    const res = await taskManagerStore.get_task_list(projectid);
    if (res && res.total !== undefined) {
      setTotal(res.total);
      // Store the total in the store for pagination (already stored in get_task_list, but ensure it's set)
      taskManagerStore.update_tm_store_prop("task_list_total", res.total);
    }
    setIsLoading(false);
  };

  const handleTaskClick = (task: any) => {
    taskManagerStore.set_task_view_modal(true);
    taskManagerStore.set_active_task(task);
  };

  const showSkeleton = isLoading || loading;

  return (
    <>
      {/* Loading State */}
      {showSkeleton ? (
        viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }, (_, i) => (
              <TaskCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <TaskTableSkeleton />
        )
      ) : filtered_task_list.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-16 px-4 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="bg-gray-100 rounded-full p-6 mb-4">
            <i className="fas fa-tasks text-gray-400 text-5xl" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            No Tasks Found
          </h3>
          <p className="text-gray-500 text-center max-w-md">
            There are no tasks to display. Create a new task to get started.
          </p>
        </div>
      ) : viewMode === "card" ? (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered_task_list.map((task: any) => (
            <TaskCard
              key={task.id}
              task={task}
              onClick={() => handleTaskClick(task)}
              onEdit={() => {
                taskManagerStore.set_active_task(task);
                taskManagerStore.set_task_edit_modal(true);
              }}
            />
          ))}
        </div>
      ) : (
        /* Table View */
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="ag-theme-quartz" style={{ width: '100%', height: '600px' }}>
            {/* @ts-ignore - AG Grid type inference issue */}
            <AgGridReact
              rowData={rowData}
              columnDefs={columnDefs}
              rowHeight={80}
              headerHeight={48}
              suppressHorizontalScroll={false}
              onRowClicked={(event) => {
                if (event.column?.getColId() !== 'actions') {
                  taskManagerStore.set_task_view_modal(true);
                  taskManagerStore.set_active_task(toJS(event.data));
                }
              }}
            />
          </div>
        </div>
      )}
    </>
  );
});
